import React, { useEffect, useMemo, useRef, useState } from "react";
import "./HostItDowntown.css";

const planSections = [
  {
    id: "concept",
    title: "Event Concept",
    paragraphs: [
      "Apartment 300 Live in Concert will be a one-night black-tie concert presenting Afrobeats, Afrohouse, Dancehall and Amapiano through a full live band. Apartment 300 will perform with local guest artists and an ensemble featuring drums, bass, guitar, brass and strings. The goal is to present music often experienced through DJs in a formal concert setting with live arrangements, professional sound, lighting and staging.",
      "The event will open with a foyer pre-show featuring local fashion, visual art, vendors and short performances before the main concert. It will be a new multidisciplinary downtown event rather than a regularly scheduled DJ night or standard bar performance.",
    ],
  },
  {
    id: "vision",
    title: "Vision and Cultural Representation",
    paragraphs: [
      "Our north star is to create a major winter cultural experience where Winnipeg’s Black creative community is represented with scale, elegance and musical excellence, while the broader public is invited to participate.",
      "The event will celebrate African and Caribbean music and create paid opportunities for local musicians, performers, designers, visual artists and production professionals. Outreach will also be made to Indigenous artists and designers for the foyer program, subject to confirmed participation.",
    ],
  },
  {
    id: "attendance",
    title: "Attendance and Marketing",
    paragraphs: [
      "Apartment 300 has a demonstrated record of drawing people downtown. Recent events at Boujee, TCM, Encore and Confusion Corner Bar & Grill have attracted crowds of more than 300 people and helped build a responsive audience that engages with the collective’s event announcements, visual campaigns and ticket releases.",
      "Promotion will use Apartment 300’s social platforms, performer networks, community partners, digital advertising and direct outreach within Winnipeg’s African, Caribbean, creative and young professional communities. Rehearsal footage, artist introductions and behind-the-scenes content will support the campaign.",
    ],
  },
  {
    id: "impact",
    title: "Downtown and Economic Impact",
    paragraphs: [
      "The event will bring an estimated 300 to 400 people downtown during the winter season. Attendees will support the venue, nearby restaurants, parking facilities, transportation providers and other downtown businesses.",
      "The production will also create paid work for local musicians, performers, technicians, videographers, photographers, security staff, designers and visual artists.",
    ],
  },
  {
    id: "media",
    title: "Media Production and Extended Reach",
    paragraphs: [
      "The event will be professionally filmed and released as an Apartment 300 concert film on YouTube. Production will include multi-camera capture, dedicated live-audio recording, editing, graphics and behind-the-scenes content.",
      "The film will be distributed through Leartify, Apartment 300’s sister media platform. A recent video connected to the platform surpassed one million YouTube views, demonstrating the team’s ability to introduce local artists, personalities and brands to audiences beyond Winnipeg.",
      "The concert film will extend the event’s impact after the live performance and create a lasting promotional asset for participating artists and downtown Winnipeg.",
    ],
  },
];

const budget = [
  ["Downtown concert venue rental", "$4,500"],
  ["Stage, sound, lighting, backline and technical production", "$4,000"],
  ["Big band musician fees", "$6,000"],
  ["Local opening performers and featured artists", "$1,500"],
  ["Fashion and art foyer pop-up", "$1,000"],
  ["Security, event insurance and permits", "$1,200"],
  ["Marketing, design and paid promotion", "$1,500"],
  ["Concert film and YouTube media production", "$4,500"],
  ["Front-of-house, ticketing and event staff", "$800"],
  ["Rehearsal, artist hospitality and local transportation", "$1,000"],
];

const images = [
  "/host-it-source/photo-01.jpg",
  "/host-it-source/photo-02.jpg",
  "/host-it-source/photo-03.jpg",
  "/host-it-source/photo-04.jpg",
  "/host-it-source/photo-05.jpg",
  "/host-it-source/photo-06.jpg",
];

export default function HostItDowntown() {
  const [activeSection, setActiveSection] = useState("concept");
  const [activeImage, setActiveImage] = useState(0);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [galleryPaused, setGalleryPaused] = useState(false);
  const swipeStart = useRef(null);
  const section = useMemo(() => planSections.find((item) => item.id === activeSection), [activeSection]);

  const showPreviousImage = () => setActiveImage((current) => (current + images.length - 1) % images.length);
  const showNextImage = () => setActiveImage((current) => (current + 1) % images.length);

  useEffect(() => {
    if (galleryPaused) return undefined;
    const interval = window.setInterval(() => setActiveImage((current) => (current + 1) % images.length), 5000);
    return () => window.clearInterval(interval);
  }, [galleryPaused]);

  const handleGestureStart = (x, y) => {
    swipeStart.current = { x, y };
    setGalleryPaused(true);
  };

  const handleGestureEnd = (x, y) => {
    if (!swipeStart.current) return;
    const deltaX = x - swipeStart.current.x;
    const deltaY = y - swipeStart.current.y;
    swipeStart.current = null;
    setGalleryPaused(false);
    if (Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    if (deltaX > 0) showPreviousImage();
    else showNextImage();
  };

  return (
    <main className="host-page">
      <nav className="host-nav" aria-label="Apartment 300">
        <a href="/" className="host-logo-link" aria-label="Apartment 300 home"><img src="/host-it-source/apt300-logo-white.png" alt="Apartment 300" /></a>
        <a href="https://www.instagram.com/apt300_/" target="_blank" rel="noreferrer">@apt300_</a>
      </nav>

      <section className="host-hero" aria-labelledby="host-title">
        <div className="host-hero-copy">
          <p className="host-eyebrow">Apartment 300 Live in Concert</p>
          <h1 id="host-title">We would love<br />to host it <em>downtown.</em></h1>
          <p className="host-subtitle">Host It Downtown High-Level Event Plan</p>
          <div className="host-details"><span>Winter 2026 (tentatively December 12th 2026)</span><span>A concert venue within the Downtown Winnipeg BIZ zone</span></div>
          <a href="#plan" className="host-scroll">Event Concept <span aria-hidden="true">↓</span></a>
        </div>
        <div className="host-hero-photo"><img src="/host-it-source/header-main.jpg" alt="Apartment 300 performer on stage" /></div>
      </section>

      <section id="plan" className="host-section host-section--plan">
        <p className="host-index">01 / HIGH-LEVEL EVENT PLAN</p>
        <div className="host-plan-intro"><h2>Apartment 300 Live in Concert</h2><p>Projected attendance: 300 to 400 people<br />Grant request: Large, $10,000<br />Total projected budget: $26,000</p></div>
        <div className="host-tabs" role="tablist" aria-label="High-Level Event Plan">
          {planSections.map((item, index) => <button key={item.id} type="button" role="tab" aria-selected={activeSection === item.id} onClick={() => setActiveSection(item.id)}><span>0{index + 1}</span>{item.title}</button>)}
        </div>
        <article className="host-plan-panel" role="tabpanel">
          <h3>{section.title}</h3>
          <div>{section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div>
        </article>
      </section>

      <section className="host-section host-section--images" aria-label="Apartment 300 event photographs">
        <p className="host-index">02 / APARTMENT 300</p>
        <div className="host-image-layout">
          <div
            className="host-image-stage"
            onTouchStart={(event) => handleGestureStart(event.touches[0].clientX, event.touches[0].clientY)}
            onTouchEnd={(event) => handleGestureEnd(event.changedTouches[0].clientX, event.changedTouches[0].clientY)}
            onTouchCancel={() => { swipeStart.current = null; setGalleryPaused(false); }}
            onMouseDown={(event) => handleGestureStart(event.clientX, event.clientY)}
            onMouseUp={(event) => handleGestureEnd(event.clientX, event.clientY)}
            onMouseEnter={() => setGalleryPaused(true)}
            onMouseLeave={() => setGalleryPaused(false)}
            onFocus={() => setGalleryPaused(true)}
            onBlur={() => setGalleryPaused(false)}
            role="group"
            aria-label="Auto-advancing event photograph gallery. Swipe left or right to change image."
          ><img src={images[activeImage]} alt={`Apartment 300 event photograph ${activeImage + 1}`} /></div>
          <div className="host-image-copy"><h2>Apartment 300 Live in Concert</h2><p>Our inspiration is Red Bull Symphonic｜Kabza De Small</p><div className="host-image-controls"><button type="button" onClick={showPreviousImage} aria-label="Previous photograph">←</button><span>{String(activeImage + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}</span><button type="button" onClick={showNextImage} aria-label="Next photograph">→</button></div></div>
        </div>
        <div className="host-thumbnails" role="tablist" aria-label="Event photographs">{images.map((image, index) => <button key={image} type="button" role="tab" aria-selected={activeImage === index} onClick={() => setActiveImage(index)}><img src={image} alt={`Select Apartment 300 event photograph ${index + 1}`} /></button>)}</div>
      </section>

      <section className="host-section host-section--budget">
        <p className="host-index">03 / WORKING APPLICATION BUDGET</p>
        <div className="host-budget-intro"><h2>Apartment 300 Live in Concert</h2><p>Working application budget. Replace estimates with venue, production and artist quotes before submission.</p></div>
        <div className="host-budget-numbers"><div><span>Host It Grant (requested)</span><b>$10,000</b></div><div><span>Total Expenditures</span><b>$26,000</b></div><div><span>Total Contributions</span><b>$26,000</b></div></div>
        <button type="button" className="host-budget-button" onClick={() => setBudgetOpen(!budgetOpen)} aria-expanded={budgetOpen}>{budgetOpen ? "Close working application budget" : "Open working application budget"}<span>{budgetOpen ? "−" : "+"}</span></button>
        {budgetOpen && <div className="host-budget-list">{budget.map(([label, amount]) => <div key={label}><span>{label}</span><b>{amount}</b></div>)}</div>}
      </section>

      <section className="host-section host-section--watch">
        <p className="host-index">04 / MEDIA PRODUCTION AND EXTENDED REACH</p>
        <div className="host-watch-intro"><h2>Our inspiration is Red Bull Symphonic｜Kabza De Small</h2><p>The event will be professionally filmed and released as an Apartment 300 concert film on YouTube.</p></div>
        <div className="host-videos">
          <iframe title="Red Bull Symphonic Kabza De Small" src="https://www.youtube-nocookie.com/embed/iGujt4PNTew" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
          <iframe title="Apartment 300 on YouTube" src="https://www.youtube-nocookie.com/embed/kgzRRJdWejY" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
        </div>
        <a className="host-instagram" href="https://www.instagram.com/apt300_/" target="_blank" rel="noreferrer">Instagram @apt300_ <span aria-hidden="true">↗</span></a>
      </section>

      <footer className="host-footer"><img src="/host-it-source/apt300-logo-white.png" alt="Apartment 300" /><p>Apartment 300 Live in Concert<br />Host It Downtown High-Level Event Plan</p></footer>
    </main>
  );
}
